import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, blockUser, assignAppRole, deleteUser } from '../api/admin.js';
import type { AppRole } from '@m8-app/contracts/auth/roles.js';

type RoleKey = 'all' | 'admin' | 'installer' | 'production_test' | 'main_user' | 'sub_main_user' | 'member' | 'sub_member';

const ROLE_TABS: { key: RoleKey; label: string }[] = [
  { key: 'all',             label: 'All' },
  { key: 'admin',           label: 'Admin' },
  { key: 'installer',       label: 'Installer' },
  { key: 'production_test', label: 'Production Test' },
  { key: 'main_user',       label: 'Main User' },
  { key: 'sub_main_user',   label: 'Sub Main User' },
  { key: 'member',          label: 'Member' },
  { key: 'sub_member',      label: 'Sub Member' },
];

const ROLE_BADGE: Record<string, string> = {
  admin:            'bg-red-50 text-red-700 border border-red-200',
  installer:        'bg-sky-50 text-sky-700 border border-sky-200',
  production_test:  'bg-orange-50 text-orange-700 border border-orange-200',
  main_user:        'bg-blue-50 text-blue-700 border border-blue-200',
  sub_main_user:    'bg-violet-50 text-violet-700 border border-violet-200',
  member:           'bg-emerald-50 text-emerald-700 border border-emerald-200',
  sub_member:       'bg-amber-50 text-amber-700 border border-amber-200',
};

const ROLE_ICON: Record<string, string> = {
  admin:            '★',
  installer:        '⚙',
  production_test:  '⚗',
  main_user:        '♛',
  sub_main_user:    '◈',
  member:           '◉',
  sub_member:       '◎',
};

const ROLE_LABEL: Record<string, string> = {
  admin:            'Admin',
  installer:        'Installer',
  production_test:  'Production Test',
  main_user:        'Main User',
  sub_main_user:    'Sub Main User',
  member:           'Member',
  sub_member:       'Sub Member',
};

const PAGE_SIZE = 10;

type UserRow = {
  id: string;
  name: string | null;
  mobile: string;
  role: string;
  status: string;
  createdAt: string;
  device?: string | null;
  since?: string;
};

export function Users() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeRole, setActiveRole] = useState<RoleKey>('all');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
  });

  const { mutate: toggleBlock } = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) => blockUser(id, { blocked }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const all = data?.users ?? [];

  const filtered = useMemo(() => {
    return all.filter((u) => {
      if (activeRole !== 'all' && u.role !== activeRole) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!u.name?.toLowerCase().includes(s) && !u.mobile.includes(s)) return false;
      }
      return true;
    });
  }, [all, activeRole, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">All Users</h2>
          <p className="text-sm text-gray-500">All users across every device registered in the system.</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <span className="text-lg leading-none">+</span> Add User
        </button>
      </div>

      {isError && (
        <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
          Failed to load users. Please refresh.
        </p>
      )}

      {/* Search + role tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">⌕</span>
          <input
            type="text"
            placeholder="Search name or mobile…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full border border-gray-300 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex bg-gray-100 rounded-full p-1">
          {ROLE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setActiveRole(t.key); setPage(1); }}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                activeRole === t.key
                  ? 'bg-white text-gray-900 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {adding && <AddUserModal onClose={() => setAdding(false)} />}
      {deleteTarget && (
        <DeleteConfirmModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 font-medium w-12">#</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Mobile</th>
              <th className="px-4 py-3 font-medium">Device</th>
              <th className="px-4 py-3 font-medium">Since</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.map((u, idx) => {
              const isBlocked = u.status === 'blocked';
              const isStaff = u.role === 'admin' || u.role === 'installer' || u.role === 'production_test';
              const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
              return (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{rowNum}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                        {ROLE_ICON[u.role] ?? '•'}
                      </span>
                      <span className={`font-medium ${isBlocked ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {u.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-700'}`}>
                      {ROLE_LABEL[u.role] ?? u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{u.mobile}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-600">
                      {(u as { device?: string }).device ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {formatSince((u as { since?: string }).since ?? u.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isBlocked
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {isBlocked ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isStaff ? (
                      <span className="text-xs text-gray-400">Staff</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleBlock({ id: u.id, blocked: !isBlocked })}
                          className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium border transition-colors ${
                            isBlocked
                              ? 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                              : 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'
                          }`}
                        >
                          {isBlocked ? '✓ Unblock' : '⊘ Block'}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(u as UserRow)}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full font-medium border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                          title="Permanently delete user"
                        >
                          🗑 Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 && !isLoading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
            )}
          </tbody>
        </table>

        {/* Footer / pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>
            {filtered.length === 0
              ? '0 of 0'
              : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              ‹
            </button>
            <span>{page}/{totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatSince(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Delete confirmation modal ─────────────────────────────────────────────────

function DeleteConfirmModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? null)
          : null;
      setErrorMsg(msg ?? 'Failed to delete user. Please try again.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-500 text-lg mt-0.5">⚠</span>
            <p className="text-sm text-red-700">
              This will <strong>permanently delete</strong> this user and remove all their data —
              devices will be unclaimed, shares revoked, and warranty records erased.
              This action cannot be undone.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-1">
            <p className="text-sm font-medium text-gray-900">{user.name ?? '(no name)'}</p>
            <p className="text-xs font-mono text-gray-500">{user.mobile}</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[user.role] ?? 'bg-gray-100 text-gray-700'}`}>
              {ROLE_LABEL[user.role] ?? user.role}
            </span>
          </div>

          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => mutate()}
            disabled={isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium"
          >
            {isPending ? 'Deleting…' : 'Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add user modal ────────────────────────────────────────────────────────────

// Roles a Super Admin can create from this panel. The mobile-app-only tiers
// (main_user / sub_main_user / member / sub_member) come in through the mobile
// app's own add-by-mobile flow — not from here.
const CREATABLE_ROLES: { value: AppRole; label: string; hint: string }[] = [
  { value: 'super_admin',     label: 'Super Admin',              hint: 'Full access — can manage other admins.' },
  { value: 'admin',           label: 'Admin',                     hint: 'Autonomix staff — manage devices, users, warranty.' },
  { value: 'installer',       label: 'Installer',                 hint: 'Dealer technician — completes on-site lift setup.' },
  { value: 'production_test', label: 'Production Test Operator',  hint: 'Factory-floor QA — used by production test harness only.' },
];

function AddUserModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<AppRole>('admin');
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const trimmedMobile = mobile.trim();
  // Loose E.164 (contract validates strictly on the server).
  const mobileValid = /^\+?[1-9]\d{7,14}$/.test(trimmedMobile);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      assignAppRole({
        mobile: trimmedMobile,
        role,
        displayName: name.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            null)
          : null;
      setErrorMsg(msg ?? 'Failed to add user. Please try again.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Add User</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="add-user-role" className="block text-sm font-medium text-gray-700 mb-1">
              User type
            </label>
            <select
              id="add-user-role"
              value={role}
              onChange={(e) => setRole(e.target.value as AppRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {CREATABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {CREATABLE_ROLES.find((r) => r.value === role)?.hint}
            </p>
          </div>

          <div>
            <label htmlFor="add-user-mobile" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile number (E.164)
            </label>
            <input
              id="add-user-mobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+91XXXXXXXXXX"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
            />
            {trimmedMobile && !mobileValid && (
              <p className="mt-1 text-xs text-red-600">Enter a valid international number, e.g. +919876543210.</p>
            )}
          </div>

          <div>
            <label htmlFor="add-user-name" className="block text-sm font-medium text-gray-700 mb-1">
              Display name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="add-user-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya Sharma"
              maxLength={80}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => mutate()}
            disabled={!mobileValid || isPending}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium"
          >
            {isPending ? 'Adding…' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}
